<template>
  <MDBCard class="py-0 border-0">
    <div v-if="loading" class="d-flex justify-content-center align-items-center py-5">
      <div class="text-center">
        <div class="spinner-border text-danger mb-3" role="status"></div>
        <p class="fw-bold">{{ $t("global.buttons.loading") }}</p>
      </div>
    </div>

    <div v-else-if="loadError" class="d-flex justify-content-center align-items-center py-5">
      <p class="fw-bold mb-0">{{ $t("bankstatement.noDataFound") }}</p>
    </div>

    <div v-else>
      <div class="overview-header">
        <h2 class="font-32 fw-light mb-4">
          {{
            $t("account.profile.inDetail", {
              name: userData.company_name || $t("account.profile.company"),
            })
          }}
        </h2>

        <MDBRow class="font-12">
          <MDBCol cols="12" md="3" class="lineHeight">
            <div class="column-title">{{ $t("account.profile.customerFile") }}</div>
            <div>{{ $t("account.profile.company") }}: {{ userData.company_name || "-" }}</div>
            <div>{{ $t("account.profile.street") }}: {{ userData.street || "-" }}</div>
            <div>{{ $t("account.profile.postcode") }}: {{ userData.postal_code || "-" }}</div>
            <div>{{ $t("account.profile.location") }}: {{ userData.city || "-" }}</div>
            <div>{{ $t("account.profile.email") }}: {{ userData.email || "-" }}</div>
            <div>{{ $t("account.profile.btw_number") }}: {{ userData.vat || "-" }}</div>
            <div>{{ $t("account.profile.iban_number") }}: {{ userData.iban || "-" }}</div>
          </MDBCol>

          <MDBCol cols="12" md="3" class="lineHeight">
            <div class="column-title">{{ $t("account.profile.customerHistory") }}</div>
            <div>
              {{ $t("account.profile.clientNrSingular") }} 1:
              {{ userData.customer_numbers?.[0] || "-" }}
            </div>
            <div>{{ $t("account.profile.registeredAt") }}: {{ formatDate(userData.registered_on) }}</div>
            <div>{{ $t("account.profile.lastLogIn") }}: {{ formatDate(userData.last_login) }}</div>
            <div>
              {{ $t("bankstatement.positivePoints") }}:
              <span class="text-success">{{ userData.points_earned || 0 }}</span>
            </div>
            <div>
              {{ $t("bankstatement.negativePoints") }}:
              <span class="text-danger">{{ userData.points_redeemed || 0 }}</span>
            </div>
            <div>
              {{ $t("bankstatement.tyresAmount") }}:
              <span>{{ userData.tyre_sales_per_year ?? 0 }}</span>
              <template v-if="userData.tire_sales_number != null">
                · {{ $t("account.profile.tireSale") }}: {{ userData.tire_sales_number }}
              </template>
            </div>
          </MDBCol>
          <MDBCol cols="12" md="3" class="lineHeight">
            <div class="column-title">{{ $t("account.profile.currentYear") }}</div>
            <div v-if="userData.customer_numbers?.length > 1">
              <div v-for="(num, idx) in userData.customer_numbers.slice(1)" :key="idx">
                {{ $t("account.profile.clientNrSingular") }} {{ idx + 2 }}: {{ num }}
              </div>
            </div>
            <template v-if="userData.show_performance_fields">
              <div>
                {{ $t("account.profile.performanceAnnualEngagement") }}:
                {{ userData.performance_annual_engagement ?? 0 }}
              </div>
              <div>
                {{ $t("account.profile.performanceVolumeToDate") }}:
                {{ userData.performance_volume_to_date ?? 0 }}
              </div>
            </template>
            <div v-if="userData.show_booster_ccs_field">
              {{ $t("account.profile.boosterCcsParticipant") }}:
              {{ userData.booster_ccs_participant ? $t("account.profile.yes") : $t("account.profile.no") }}
            </div>
          </MDBCol>

          <MDBCol cols="12" md="3" class="lineHeight">
            <div class="column-title">{{ $t("bankstatement.currentPointState") }}</div>
            <div class="text-success font-24">
              {{ userData.current_points_status || 0 }} {{ $t("bankstatement.points") }}
            </div>
            <template v-if="userData.show_points_status_split">
              <div class="font-14 mt-2">
                <div>
                  {{ $t("bankstatement.unlockedPoints") }}:
                  <span class="text-success">
                    {{ userData.current_unlocked_points_status || 0 }} {{ $t("bankstatement.points") }}
                  </span>
                </div>
                <div>
                  {{ $t("bankstatement.lockedPoints") }}:
                  <span>{{ userData.current_locked_points_status || 0 }} {{ $t("bankstatement.points") }}</span>
                </div>
              </div>
            </template>
            <div class="font-16 mt-2" v-if="userData.bonus_enabled">{{ $t("bankstatement.currentKickbackState") }}:
            </div>
            <div class="text-success font-24" v-if="userData.bonus_enabled">{{ userData.current_bonus_status || 0 }} €
            </div>
          </MDBCol>
        </MDBRow>

        <MDBRow class="mt-4">
          <MDBCol cols="12" md="6" v-if="userData.show_wholesaler_filter">
            <label class="label px-3 text-uppercase">{{ $t("bankstatement.typeFilter") }}</label>
            <select class="form-select mb-2" v-model="selectedWholesaler" @change="applyFilters">
              <option :value="null">{{ $t("global.makeChoice") }}</option>
              <option v-for="(w, i) in wholesalers" :key="i" :value="w.id">{{ w.name }}</option>
            </select>
          </MDBCol>

          <MDBCol cols="12" :md="userData.show_wholesaler_filter ? 6 : 12">
            <label class="label px-3 text-uppercase">{{ $t("bankstatement.dateFilter") }}</label>
            <VueDatePicker
              v-model="dateRange"
              month-picker
              range
              :min-date="yearStart"
              :max-date="yearEnd"
              prevent-min-max-navigation
              :enable-time-picker="false"
              format="MM/yyyy"
              @update:model-value="applyFilters"
            />
          </MDBCol>
        </MDBRow>

        <DealerTableWidget :data="dealerListPayload" :user-id="props.user_id" />
      </div>
    </div>
  </MDBCard>
</template>

<script setup>
import { ref, onMounted, defineProps, watch } from "vue";
import { MDBCard, MDBRow, MDBCol } from "mdb-vue-ui-kit";
import VueDatePicker from "@vuepic/vue-datepicker";
import DealerTableWidget from "../../Widgets/DealerTableWidget.vue";
import { KpiDashboardService } from "../../../services/kpi-dashboard.service";

const props = defineProps({ user_id: { type: String, required: true } });

const loading = ref(true);
const loadError = ref(false);
const userData = ref({});
const dealerListPayload = ref({
  data: [],
  total: 0,
  per_page: 20,
  page: 1,
  pages: 1,
  metadata: {},
});
const wholesalers = ref([]);
const selectedWholesaler = ref(null);
const filterYear = new Date().getFullYear();
const yearStart = new Date(filterYear, 0, 1);
const yearEnd = new Date(filterYear, 11, 31);
const currentYearRange = () => [
  { month: 0, year: filterYear },
  { month: 11, year: filterYear },
];
const dateRange = ref(currentYearRange());
const formatDate = (d) => (d ? new Date(d).toLocaleDateString() : "-");

const fetchCustomerDetails = async (filters = {}) => {
  loading.value = true;
  loadError.value = false;

  try {
    const query = new URLSearchParams();

    if (filters.wholesaler_id) query.append("filters[wholesaler_id]", filters.wholesaler_id);
    if (filters.date_from) query.append("filters[start_date]", filters.date_from);
    if (filters.date_to) query.append("filters[end_date]", filters.date_to);

    const data = await KpiDashboardService.fetchCustomerDetails(props.user_id, query.toString());
    if (!data?.details) {
      loadError.value = true;
      return;
    }

    userData.value = data.details;
    dealerListPayload.value = data.dealer_list || {
      data: [],
      total: 0,
      per_page: 20,
      page: 1,
      pages: 1,
      metadata: {},
    };

    wholesalers.value = data.wholesalers;
  } catch (err) {
    loadError.value = true;
    console.error("Error fetching customer details:", err);
    dealerListPayload.value = {
      data: [],
      total: 0,
      per_page: 20,
      page: 1,
      pages: 1,
      metadata: {},
    };
  } finally {
    loading.value = false;
  }
};

const toDateParam = (value) => {
  const date = new Date(value);
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

const monthEdges = (range) => {
  if (!Array.isArray(range) || range.length !== 2) {
    return null;
  }
  const [from, to] = range;
  if (from?.month == null || to?.month == null || Number(from.year) !== filterYear || Number(to.year) !== filterYear) {
    return null;
  }
  const startMonth = Math.min(Number(from.month), Number(to.month));
  const endMonth = Math.max(Number(from.month), Number(to.month));
  return [new Date(filterYear, startMonth, 1), new Date(filterYear, endMonth + 1, 0)];
};

const applyFilters = async () => {
  const edges = monthEdges(dateRange.value);
  if (!edges) {
    dateRange.value = currentYearRange();
  }
  const [from, to] = edges ?? [yearStart, yearEnd];
  await fetchCustomerDetails({
    wholesaler_id: selectedWholesaler.value,
    date_from: toDateParam(from),
    date_to: toDateParam(to),
  });
};

onMounted(applyFilters);

watch(() => props.user_id, () => {
  dateRange.value = currentYearRange();
  applyFilters();
});
</script>

<style scoped>
.overview-header {
  padding: 40px 60px;
}

.spinner-border {
  width: 3rem;
  height: 3rem;
}

.lineHeight div {
  line-height: 22.4px;
}

.column-title {
  font-size: 16px;
  font-weight: 600;
  line-height: 22.4px;
  margin-bottom: 8px;
  min-height: 22.4px;
}
</style>
